import { useMemo } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { formatMinutes, paletteFor } from "../lib/calendarUtils";
import { resolveStepTimers } from "../lib/inferTimersFromInstruction";

// StepTimeline — horizontal-list-of-bands view of a recipe's steps,
// each band sized proportionally to its step.timer (with no-timer
// steps getting a small default share so they remain visible).
// Used by MealDetailDrawer as the "drill-down" surface that shows
// what the recipe actually does, broken down by phase + duration.
//
// Compact variant: each step is a single line — colored bullet on
// the left, step title in the middle, duration on the right.
// Total cook time renders at the top.
export default function StepTimeline({ recipe }) {
  const breakdown = useMemo(() => buildBreakdown(recipe), [recipe]);
  if (!breakdown) return null;
  const { steps, totalMinutes } = breakdown;

  return (
    <div style={{
      marginTop: 14,
      padding: "12px 14px",
      background: COLOR.deep,
      border: `1px solid ${COLOR.edge}`,
      borderRadius: 12,
    }}>
      <div style={{
        display: "flex", alignItems: "baseline", justifyContent: "space-between",
        gap: 12, marginBottom: 10,
      }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
          color: COLOR.gold, letterSpacing: "0.16em",
        }}>
          STEP TIMELINE
        </div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
          color: COLOR.muted, letterSpacing: "0.04em",
        }}>
          Total · {formatMinutes(totalMinutes)}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {steps.map((s, i) => {
          const palette = paletteFor(s.index);
          // Width of the duration bar relative to the longest step
          // (so the user gets a quick visual sense of which phases
          // dominate). Min 6% so very short steps stay visible.
          const widthPct = Math.max(6, Math.round(s.weight * 100));
          return (
            <div key={i} style={{
              display: "flex", alignItems: "center", gap: 10,
              fontFamily: FONT.forma,
            }}>
              <div style={{
                fontSize: 10, fontWeight: 700,
                color: palette.fg, letterSpacing: "0.06em",
                width: 22, flexShrink: 0,
              }}>
                {String(i + 1).padStart(2, "0")}
              </div>
              <div style={{
                flex: 1, minWidth: 0,
                display: "flex", flexDirection: "column", gap: 4,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 500,
                  color: COLOR.ink,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {s.title}
                </div>
                <div style={{
                  height: 6, borderRadius: 999,
                  background: "rgba(255,255,255,0.5)",
                  overflow: "hidden",
                }}>
                  <div style={{
                    height: "100%",
                    width: `${widthPct}%`,
                    background: palette.fg,
                    opacity: 0.85,
                    borderRadius: 999,
                  }} />
                </div>
              </div>
              <div style={{
                fontSize: 11, fontWeight: 600,
                color: palette.fg, letterSpacing: "0.02em",
                flexShrink: 0,
                width: 56, textAlign: "right",
              }}>
                {formatMinutes(s.minutes)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildBreakdown(recipe) {
  const steps = Array.isArray(recipe?.steps) ? recipe.steps : null;
  if (!steps || steps.length === 0) return null;
  const NO_TIMER_DEFAULT_SEC = 60;
  // Resolve each step's duration the SAME way cook mode does — via
  // resolveStepTimers, which (a) sums an explicit step.timers[] array,
  // and (b) INFERS timers from the instruction prose ("simmer for 20
  // minutes") when no numeric field is present. Reading raw s.timer
  // alone made every step that relied on prose-inferred timing collapse
  // to the 60s default, so the whole preview read "1m · 1m · 1m".
  const weightsRaw = steps.map(s => {
    const resolved = resolveStepTimers(s);
    let sec = 0;
    if (resolved?.timers?.length) {
      sec = resolved.timers.reduce(
        (sum, t) => sum + (Number(t?.seconds) > 0 ? Number(t.seconds) : 0),
        0,
      );
    } else if (Number(resolved?.timer) > 0) {
      sec = Number(resolved.timer);
    }
    return sec > 0 ? sec : NO_TIMER_DEFAULT_SEC;
  });
  const max = Math.max(...weightsRaw);
  const totalSec = weightsRaw.reduce((a, b) => a + b, 0) || 1;
  const totalMinutes = Math.round(totalSec / 60);
  return {
    totalMinutes,
    steps: steps.map((s, i) => ({
      index: i,
      // Bar width relative to the longest step in this recipe so
      // the visual cue is "this step is dominant" vs "this step is
      // short," not "this is the absolute share of total."
      weight: weightsRaw[i] / max,
      durationSec: weightsRaw[i],
      minutes: Math.round(weightsRaw[i] / 60),
      title: s?.title || `Step ${i + 1}`,
    })),
  };
}
